import { EditTodoIcon } from '@/assets/icons/info-icons/editTodo';
import { TierDropdown } from '@/components/dueDiligence-page/dueDiligenceRecord/tierDropdown';
import Header from '@/components/header';
import { GoBackButton } from '@/components/header/goBackButton';
import { PageTitle } from '@/components/header/pageTitle';
import { useResponsive } from '@/hooks/use-responsive';
import { useCustomizationsData } from '@/hooks/useCustomizationsData';
import { MainWrap, PaddingWrap } from '@/styles/common';
import { themeVar } from '@/styles/theme';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { DefaultTheme } from 'styled-components/dist/types';
import { getCacheBustedFileUrl } from '../../utils';
import { FieldValueWrapper, InfoRowCell, RowWrapper } from '../catalogBanner';
import { IFirmSettings } from '../templatesSettings/types';
import { EditColors } from './editColors';
import { ThemeUploadLogo } from './uploadLogo';

const logosItems: IFirmSettings[] = [
  { id: null, fieldName: 'Log In Logo', key: 'loginLogo', value: '' },
  { id: null, fieldName: 'Nav Bar Logo', key: 'navbarLogo', value: '' },
  { id: null, fieldName: 'Nav Bar Simple Logo', key: 'navbarSimpleLogo', value: '' },
  { id: null, fieldName: 'Email Logo', key: 'emailLogo', value: '' }
];

const colorsItems = [
  { title: 'Important Button', themeVariableName: 'action.primary', value: '' },
  { title: 'Default Button', themeVariableName: 'action.default', value: '' },
  { title: 'Button Hover State', themeVariableName: 'action.hover', value: '' },
  { title: 'Selected Item', themeVariableName: 'action.active', value: '' },
  { title: 'Text Link', themeVariableName: 'font.action', value: '' },
  { title: 'Nav Background', themeVariableName: 'header.base', value: '' },
  { title: 'Action Nav Item', themeVariableName: 'menu.active', value: '' },
  { title: 'Nav Labels', themeVariableName: 'header.action', value: '' }
];

const getNestedThemeColor = (theme: DefaultTheme, path: string): string => {
  const result = path.split('.').reduce((acc, part) => {
    if (acc && typeof acc === 'object') {
      return acc[part];
    }
    return undefined;
  }, theme);

  return typeof result === 'string' ? result : '';
};

export const getLogoSpecifications = (key: string): string[] => {
  const logoSpecifications: { [key: string]: string[] } = {
    loginLogo: ['1:1 Ratio', 'Solid Color', 'Transparent Background', 'SVG'],
    navbarLogo: ['5:1 Ratio (width:height)', 'Solid Color', 'Transparent Background', 'SVG'],
    navbarSimpleLogo: ['1:1 Ratio', 'Solid Color', 'Transparent Background', 'SVG'],
    emailLogo: ['Solid Color', 'Transparent Background', 'PNG, WEBP, JPEG, JPG']
  };

  return logoSpecifications[key] || [];
};

const imageWidths: { [key: string]: number } = {
  loginLogo: 140,
  navbarLogo: 142,
  navbarSimpleLogo: 60,
  emailLogo: 140
};

interface CatalogBannerProps {
  backToTitle: string;
  handleClose: () => void;
}

export const ThemeSettings = ({ backToTitle, handleClose }: CatalogBannerProps) => {
  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  const { data: customizationsData } = useCustomizationsData();
  const [searchParams, setSearchParams] = useSearchParams();

  const action = searchParams.get('action') || '';
  const type = searchParams.get('type') || '';
  const windowType = searchParams.get('windowType') || 'default';
  const editRowKey = searchParams.get('edit') || '';

  const [logosData, setLogosData] = useState<IFirmSettings[]>(logosItems);
  const [colorsData, setColorsData] = useState([]);
  const [currentRow, setCurrentRow] = useState<IFirmSettings | null>(null);
  const [scrollTo, setScrollTo] = useState(0);

  useEffect(() => {
    const updatedColorsItems = colorsItems.map((item) => {
      return { ...item, value: getNestedThemeColor(customizationsData?.theme, item.themeVariableName) };
    });

    setColorsData(updatedColorsItems);
  }, [customizationsData?.theme]);

  useEffect(() => {
    const updatedLogosItems = logosItems.map((item) => {
      const matchedLogoItem = customizationsData?.logos?.[item.key];

      if (matchedLogoItem) {
        return { ...item, value: matchedLogoItem };
      }
      return item;
    });

    setLogosData(updatedLogosItems);
  }, [customizationsData?.logos]);

  useEffect(() => {
    if (!editRowKey) return;

    const row = logosData?.find((item) => item.key === editRowKey);
    setCurrentRow(row || null);
  }, [logosData]);

  useEffect(() => {
    if (windowType === 'default') {
      window.scrollTo(0, scrollTo);
    }
  }, [windowType]);

  const handleRedirect = (windowType: string, key: string, row?: IFirmSettings) => {
    const queryParams = new URLSearchParams({
      action,
      ...(type && { type }),
      windowType,
      edit: key
    });

    navigate(`/settings?${queryParams.toString()}`);
    setScrollTo(window.scrollY);
    setCurrentRow(row || null);
  };

  const handleCloseEdit = () => {
    const queryParams = new URLSearchParams({
      action,
      ...(type && { type })
    });

    navigate(`/settings?${queryParams.toString()}`);
    setCurrentRow(null);
  };

  return (
    <>
      {windowType === 'upload-logo' && <ThemeUploadLogo currentRow={currentRow} handleClose={handleCloseEdit} />}
      {windowType === 'edit-colors' && <EditColors colorsData={colorsData} handleClose={handleCloseEdit} />}
      {windowType === 'default' && (
        <>
          <MainWrap>
            <Header modalControl={<GoBackButton handleClose={handleClose} backToTitle={backToTitle} />} />
            <PageTitle title="Theme" />
          </MainWrap>
          <MainWrap>
            <PaddingWrap>
              <SettingsContainer>
                <TierDropdown title="Logos">
                  {logosData?.map((item) => (
                    <RowWrapper key={item.fieldName} disabled={false} isMobile={isMobile}>
                      <InfoRowCell>{item.fieldName}</InfoRowCell>
                      <CatalogBannerImageWrapper>
                        {item.value ? (
                          <FieldValueWrapper>
                            {item.value && (
                              <ImageWrapper width={imageWidths[item.key]}>
                                <ImageItem src={getCacheBustedFileUrl(item.value)} alt={item.fieldName} />
                              </ImageWrapper>
                            )}
                          </FieldValueWrapper>
                        ) : (
                          <NoLogoText>No logo has been added.</NoLogoText>
                        )}
                        <LogoSpecifications>
                          Logo Specifications:
                          <LogoSpecificationsUnorderedList>
                            {getLogoSpecifications(item.key)?.map((specification) => <li key={specification}>{specification}</li>)}
                          </LogoSpecificationsUnorderedList>
                        </LogoSpecifications>
                      </CatalogBannerImageWrapper>
                      <EditTodoButton isMobile={isMobile} id="editTodoButton" onClick={() => handleRedirect('upload-logo', item.key, item)}>
                        <EditTodoIcon />
                      </EditTodoButton>
                    </RowWrapper>
                  ))}
                </TierDropdown>
                <TierDropdown title="Colors">
                  <ColorsWrapper>
                    <ColorItems>
                      {colorsData?.map((item) => (
                        <ColorItem key={item.themeVariableName}>
                          <InfoRowCell>{item.title}</InfoRowCell>
                          <FieldValueWrapper>
                            <ColorValue>
                              <ColorPreview bgColor={item.value} />
                              {item.value}
                            </ColorValue>
                          </FieldValueWrapper>
                        </ColorItem>
                      ))}
                    </ColorItems>
                    <EditTodoButton isMobile={isMobile} id="editTodoButton" onClick={() => handleRedirect('edit-colors', 'colors')}>
                      <EditTodoIcon />
                    </EditTodoButton>
                  </ColorsWrapper>
                </TierDropdown>
              </SettingsContainer>
            </PaddingWrap>
          </MainWrap>
        </>
      )}
    </>
  );
};

const SettingsContainer = styled.div`
  margin: 30px 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const CatalogBannerImageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const NoLogoText = styled.p`
  padding: 10px 0;
  font-size: 19px;
  font-weight: 400;
  line-height: 26.6px;
  color: ${themeVar('font.weak')};
`;

const LogoSpecifications = styled.div`
  font-size: 16px;
  font-weight: 400;
  line-height: 22.4px;
  color: ${themeVar('font.base')};
`;

const LogoSpecificationsUnorderedList = styled.ul`
  margin-left: 25px;
  list-style-type: square;
`;

const ColorsWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 28px 12px;
  border-bottom: 1px solid ${themeVar('border.base')};

  &:hover #editTodoButton {
    opacity: 1;
  }

  &:hover {
    background-color: ${themeVar('layer.2')};
    transition: background-color 100ms ease;
  }
`;

const ColorItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const ColorItem = styled.div`
  display: flex;
`;

const ColorValue = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ColorPreview = styled.div<{ bgColor: string }>`
  width: 20px;
  height: 20px;
  border-radius: 2px;
  border: 1px solid ${themeVar('border.base')};
  background-color: ${({ bgColor }) => bgColor};
`;

const EditTodoButton = styled.div<{ isMobile: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  margin-left: auto;
  cursor: pointer;
  height: 40px;
  width: ${({ isMobile }) => (isMobile ? '100%' : '40px')};
  background-color: ${themeVar('action.default')};
  opacity: ${({ isMobile }) => (isMobile ? '1' : '0')};
`;

const ImageWrapper = styled.div<{ width: number }>`
  display: flex;
  justify-content: center;
  align-items: center;
  max-width: ${({ width }) => `${width}px`};
  overflow: hidden;
  border-radius: 5px;
  padding: 10px;
  background-color: ${themeVar('menu.tab')};
`;

const ImageItem = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;
